<template>
  <ScaleLoader v-if="loading" />
  <div v-else>
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    />
    <div class="vehicle-report-grid-card">
      <DxDataGrid
        :ref="dataGridRef"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        class="vehicle-report-grid"
        :data-source="store"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="false" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.reports.weeklyReport.weeklyReport')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.weeklyReport.date')"
            :filter-operations="allowedOperations"
            :customize-text="dateRangeCell"
            data-field="date_from"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.weeklyReport.weekNumber')"
            :filter-operations="allowedOperations"
            data-field="week_number"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.weeklyReport.drivingTime')"
            :filter-operations="allowedOperations"
            :customize-text="formatMinutesCell"
            data-field="drive_time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.weeklyReport.additionalInfo')"
            :filter-operations="allowedOperations"
            cell-template="additional-info-cell-template"
            data-field="place_start"
          />
        </DxColumn>
        <template #additional-info-cell-template="{ data }">
          <p>Start: {{ data.data.place_start }}</p>
          <p>Stop: {{ data.data.place_stop }}</p>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { getWeeklyReport } from '@/crud/getters'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid as exportDataGridToExcel } from 'devextreme/excel_exporter'
import { getHumanDate, minutesToHoursMinutes } from '@/utils/tools'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import { importFont } from '@/utils/jspdf/importFont'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import CustomStore from 'devextreme/data/custom_store'
import { buildGridLoadParams, normalizeGridResponse } from '@/services/reports/grid-store'

export default defineComponent({
  name: 'WeeklyReport',
  components: {
    ExportButtons,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object,
    reportType: String
  },
  data(): {
    store: any,
    cardStore: Array<any>,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: string,
    loading: boolean
  } {
    return {
      store: [],
      cardStore: [],
      allowedOperations: ['contains', '=', 'notcontains', '!=', '>', '>=', '<', '<='],
      remoteOperations: true,
      dataGridRef: 'weeklyReportGrid',
      loading: true
    }
  },
  computed: {
    weeklyReportGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef].instance
    }
  },
  methods: {
    getHumanDate: getHumanDate,
    minutesToHoursMinutes: minutesToHoursMinutes,
    dateRangeCell(cellInfo: any): string {
      return `${getHumanDate(cellInfo.data.date_from)} - ${getHumanDate(cellInfo.data.date_to)}`
    },
    formatMinutesCell(cellInfo: any): string {
      return minutesToHoursMinutes(cellInfo.value || 0)
    },
    exportGridsToExcel(extension: string) {
      const self: any = this
      const workbook = new Workbook()

      const sheet = workbook
          .addWorksheet(self.$t('components.reports.tabs.reports'))

      exportDataGridToExcel({
        worksheet: sheet,
        component: self.weeklyReportGridInstance
      })
          .then(() => {
            if (extension == 'csv')
              workbook.csv.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Weekly_report_${ self.imei }.csv`)
              })
            else if (extension == 'xlsx')
              workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Weekly_report_${ self.imei }.xlsx`)
              })
          })
    },
    exportGridsToPdf() {
      const doc = new jsPDF()
      importFont(doc)

      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: this.weeklyReportGridInstance,
        // autoTableOptions: {
        //   styles: {
        //     font: 'Roboto'
        //   }
        // }
      })
          .then(() => {
            doc.save(`Weekly_report_${ this.imei }.pdf`)
          })
    }
  },
  mounted() {
    if (this.imei && this.dateFrom && this.dateTo) {
      this.store = new CustomStore({
        load: (loadOptions: any) => getWeeklyReport(
            this.imei as bigint,
            this.dateFrom as string,
            this.dateTo as string,
            buildGridLoadParams(loadOptions)
        ).then((res) => normalizeGridResponse(res.data)).catch(() => {
          ;(this as any).$swal.fire({
            icon: 'warning',
            title: 'Oops...',
            text: this.$t('components.error')
          })
          return {data: [], totalCount: 0}
        })
      })
      this.loading = false
    }
  }
})
</script>
<style lang="scss" scoped src="@/assets/scss/report-grid.scss"></style>
